import React, { useState } from "react"
import { Button, Form, Modal } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import _ from "lodash"
import { Formik } from "formik"
import { v4 as uuid } from "uuid"

import Api from "@/api"
import AsyncSearch, { DataStates } from "@/helpers/AsyncSearch"
import { getView } from "@/onboarding/features/product/components/product_components/views/EditWrapper"
import { appendFieldListItem } from "@/onboarding/features/product/Slice"
import { getComponentFieldName } from "@/onboarding/features/product/models/Component"
import LoadingSpinner from "@/components/LoadingSpinner"
import { getProduct } from "@/onboarding/features/product/selectors"

const getComponent = ({ components }) => {
  if (_.isEmpty(components)) {
    return {}
  }

  return _.merge(_.head(components).value.document, { id: uuid() })
}

const componentSelected = (values) => !_.isEmpty(getComponent(values))

const ComponentPreview = ({ component }): JSX.Element => {
  const View = getView(component)

  return <View component={component} />
}

const ImportComponentModal = ({
  show,
  onHide,
  componentType,
}: {
  show: boolean
  onHide: () => void
  componentType: string
}): JSX.Element => {
  const dispatch = useDispatch()
  const product = useSelector(getProduct)

  let getOptions

  if (_.isUndefined(componentType)) {
    getOptions = (query) => Api.onboardingProduct.searchComponents(query, product.port.id)
  } else {
    getOptions = (query) =>
      Api.onboardingProduct.searchComponentByType(query, product.port.id, componentType)
  }

  const [dataState, setDataState] = useState(() => DataStates.LOADING)

  const onOptionsLoaded = (newDataState) => {
    console.log(`${dataState}->${newDataState}`)
    setDataState(newDataState)
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="import-component-modal-title"
      centered
    >
      <Modal.Header>
        <Modal.Title id="import-component-modal-title">Import Component</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column gap-3">
        <Formik
          initialValues={{ components: [] }}
          onSubmit={(values, { setSubmitting }) => {
            if (!componentSelected(values)) {
              return
            }

            setSubmitting(true)

            const component = getComponent(values)

            dispatch(
              appendFieldListItem({
                fieldName: getComponentFieldName(component.kind),
                value: component,
              })
            )

            setSubmitting(false)
            onHide()
          }}
        >
          {({ handleSubmit, setValues, values, isSubmitting }) => (
            <Form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
              {(() => {
                switch (dataState) {
                  case DataStates.LOADING:
                    return (
                      <p className="mb-0">
                        Loading components...
                        <LoadingSpinner />
                      </p>
                    )
                  case DataStates.ABSENT:
                    return (
                      <p className="mb-0">
                        No components of the required type are available at this port.
                      </p>
                    )
                  case DataStates.PRESENT:
                    return (
                      <p className="mb-0">
                        Search below to import a component from your component library for use in
                        this tour.
                      </p>
                    )
                  default:
                    return null
                }
              })()}
              <Form.Group>
                <AsyncSearch
                  selectedValues={values.components}
                  setSelectedValues={(components) => setValues({ components })}
                  getOptions={getOptions}
                  placeholder="Start typing to search..."
                  onOptionsLoaded={onOptionsLoaded}
                  absentDataMessage="No options available."
                  dataState={dataState}
                  maxSelectable={1}
                />
              </Form.Group>
              {componentSelected(values) && <ComponentPreview component={getComponent(values)} />}
              {isSubmitting ? (
                <LoadingSpinner />
              ) : (
                <div className="d-flex gap-3">
                  <Button type="submit" disabled={!componentSelected(values)}>
                    Submit
                  </Button>
                  <Button variant="danger" onClick={onHide}>
                    Cancel
                  </Button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  )
}

export default ImportComponentModal
